import { useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { PAPER_SIZES } from '@/constants/printOptions';
import type { PaperSizeId, PrintQuality } from '@/types/index';

interface AdvancedSettingsPanelProps {
  paperSizeId: PaperSizeId;
  isDuplex: boolean;
  quality: PrintQuality;
  collate: boolean;
  staple: boolean;

  supportsDuplex: boolean;
  supportsA4: boolean;
  supportsLetter: boolean;
  supportsLegal: boolean; 

  onPaperSizeChange: (id: PaperSizeId) => void;
  onDuplexChange: (value: boolean) => void;
  onQualityChange: (value: PrintQuality) => void;
  onCollateChange: (value: boolean) => void;
  onStapleChange: (value: boolean) => void;

  duplexDiscountRate: number;
  duplexDisabled?: boolean;
  duplexDisabledReason?: string;
}

export function AdvancedSettingsPanel({
  paperSizeId,
  isDuplex,
  // quality,
  // collate,
  // staple,
  supportsDuplex,
  supportsA4,
  supportsLetter,
  supportsLegal, 
  onPaperSizeChange,
  onDuplexChange,
  duplexDiscountRate,
  duplexDisabled = false,
  duplexDisabledReason = 'Duplex needs at least 2 pages',
  // onQualityChange,
  // onCollateChange,
  // onStapleChange,
}: AdvancedSettingsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const activePaper = PAPER_SIZES.find((p) => p.id === paperSizeId);

const paperGrid = (
  <div className="row row-cols-2 g-2">
    {PAPER_SIZES.map((size) => {
      const isSupported =
        size.id === 'a4'
          ? supportsA4
          : size.id === 'letter'
            ? supportsLetter
            : size.id === 'legal'
              ? supportsLegal
              : false;

      if (!isSupported) return null;

      return (
        <div className="col" key={size.id}>
          <button
            type="button"
            className={`btn w-100 h-100 border-0 rounded-3 d-flex align-items-center justify-content-between text-start p-2 p-lg-3 ${
              paperSizeId === size.id
                ? 'bg-surface-high text-primary shadow-sm'
                : 'bg-surface-low text-on-surface-variant'
            }`}
            onClick={() => onPaperSizeChange(size.id)}
          >
            <span className="d-flex flex-column">
              <span
                className={`text-label-sm fw-bold ${
                  paperSizeId === size.id
                    ? 'text-primary'
                    : 'text-on-surface'
                }`}
              >
                {size.label}
              </span>

              <span className="text-label-sm text-on-surface-variant">
                {size.sub}
              </span>
            </span>

            <span className="text-label-sm">
              {paperSizeId === size.id ? (
                <i className="bi bi-check-circle-fill" />
              ) : (
                `+₹${size.priceDelta.toFixed(2)}`
              )}
            </span>
          </button>
        </div>
      );
    })}
  </div>
);

  const duplexToggle = (
    <div
      className="d-flex align-items-center justify-content-between p-2 p-lg-3 rounded-3 bg-surface-low"
      style={duplexDisabled ? { opacity: 0.6 } : undefined}
    >
      <div className="d-flex align-items-center gap-2">
        <div
          className="rounded-3 bg-surface-lowest d-flex align-items-center justify-content-center text-primary flex-shrink-0"
          style={{ width: 36, height: 36 }}
        >
          <i className="bi bi-journal-richtext" />
        </div>
        <div className="d-flex flex-column">
          <span className="text-label-md text-on-surface">Double-Sided (Duplex)</span>
          {duplexDisabled ? (
            <span className="text-body-sm text-on-surface-variant">{duplexDisabledReason}</span>
          ) : (
            <span className="text-body-sm text-success fw-medium">
              Saves paper • {(duplexDiscountRate * 100).toFixed(1)}% eco discount
            </span>
          )}
        </div>
      </div>
      <div className="form-check form-switch m-0">
        <input
          className="form-check-input tap-target"
          type="checkbox"
          role="switch"
          checked={isDuplex && !duplexDisabled}
          disabled={duplexDisabled}
          onChange={(e) => onDuplexChange(e.target.checked)}
          style={{ width: 44, height: 24 }}
        />
      </div>
    </div>
  );


return (
  <>
    {/* Mobile: accordion */}
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-sm overflow-hidden d-lg-none">
      <button
        type="button"
        className="btn w-100 p-3 d-flex align-items-center justify-content-between border-0 rounded-0 text-start"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center text-on-surface-variant"
            style={{ width: 32, height: 32 }}
          >
            <i className="bi bi-sliders" />
          </div>

          <div className="d-flex flex-column">
            <span className="text-label-md text-on-surface">
              Advanced Print Settings
            </span>

            <span className="text-body-sm text-on-surface-variant">
              {activePaper?.label} • {isDuplex && !duplexDisabled ? 'Duplex On' : 'Duplex Off'}
            </span>
          </div>
        </div>

        <i
          className={`bi ${
            expanded ? 'bi-chevron-up' : 'bi-chevron-down'
          } text-on-surface-variant`}
        />
      </button>

      <Collapse in={expanded}>
        <div className="px-3 pb-3 d-flex flex-column gap-3">
          <div>
            <label className="text-label-sm text-on-surface-variant d-block mb-2">
              Paper Dimensions
            </label>

            {paperGrid}
          </div>

          {supportsDuplex && duplexToggle}

          <div>
            {/* <label className="text-label-sm text-on-surface-variant d-block mb-2">
              Output Quality
            </label> */}
            {/* {qualityGrid} */}
          </div>
        </div>
      </Collapse>
    </section>

    {/* Desktop: same UI, larger dimensions */}
    <section className="bg-surface-lowest rounded-4 shadow-kiosk-md p-4 d-none d-lg-block">
      <div className="d-flex align-items-center gap-2 mb-4">
        <div
          className="rounded-3 bg-surface-container d-flex align-items-center justify-content-center text-on-surface-variant"
          style={{ width: 40, height: 40 }}
        >
          <i className="bi bi-sliders" />
        </div>

        <div>
          <h3 className="text-headline-sm text-on-surface fw-bold mb-0">
            Advanced Print Settings
          </h3>

          <p className="text-body-sm text-on-surface-variant mb-0">
            Configure paper and printing options
          </p>
        </div>
      </div>

      <div className="d-flex flex-column gap-4">
        <div>
          <label className="text-label-sm text-on-surface-variant d-block mb-2">
            Paper Dimensions
          </label>

          {paperGrid}
        </div>

        {supportsDuplex && duplexToggle}

        <div>
          {/* <label className="text-label-sm text-on-surface-variant d-block mb-2">
            Output Quality
          </label> */}
          {/* {qualityGrid} */}
        </div>
      </div>
    </section>
  </>
);
}
